/**
 * Commentable — Frame.io-style comment affordance shared by the video storyboard
 * (StoryboardDetail) and the assessment blueprint (AssessmentWorkspaceShell).
 *
 * Wrap any note in <Commentable>: it adds a 💬 button that opens an inline composer,
 * renders queued comments under the note, and pushes each comment into the
 * outlineEditQueueStore so it surfaces as a pill above the Ivy composer and rides
 * along on the next Send.
 */
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { MessageSquarePlus, X } from 'lucide-react';
import { useOutlineEditQueueStore, type OutlineEditItem } from '@/stores/outlineEditQueueStore';

export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-zinc-500 font-medium mb-0.5">{children}</div>;
}

interface CommentableProps {
  anchorId: string;
  anchorLabel: string;
  sceneLabel: string;
  conversationId?: string;
  slideId: string;
  comments: OutlineEditItem[];
  children: React.ReactNode;
}

export function Commentable({ anchorId, anchorLabel, sceneLabel, conversationId, slideId, comments, children }: CommentableProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const addComment = useOutlineEditQueueStore((s) => s.addComment);
  const remove = useOutlineEditQueueStore((s) => s.remove);

  const submit = () => {
    const t = text.trim();
    if (!t || !conversationId) return;
    addComment(conversationId, { slideId, sceneLabel, anchorId, anchorLabel, text: t });
    setText('');
    setOpen(false);
  };

  return (
    <div className="group/c relative rounded-lg pl-2.5 pr-7 py-1.5 -ml-1 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors">
      {children}
      <button
        onClick={() => setOpen((v) => !v)}
        title={`Comment on ${anchorLabel}`}
        className={cn(
          'absolute top-1.5 right-1 p-1 rounded-md transition-all',
          comments.length > 0
            ? 'text-primary opacity-100'
            : 'text-gray-400 dark:text-zinc-500 opacity-50 group-hover/c:opacity-100 hover:text-primary hover:bg-accent dark:hover:bg-primary/10',
        )}
      >
        <MessageSquarePlus className="w-3.5 h-3.5" />
      </button>

      {comments.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {comments.map((c) => (
            <li key={c.id} className="flex items-start gap-1.5 text-[12px] bg-accent/70 dark:bg-primary/10 rounded-md px-2 py-1">
              <span className="flex-1 text-accent-foreground">{c.message.replace(/^Feedback on .*?: /, '')}</span>
              {conversationId && (
                <button onClick={() => remove(conversationId, c.id)} className="p-0.5 rounded text-primary hover:text-destructive"><X className="w-3 h-3" /></button>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && conversationId && (
        <div className="mt-1.5">
          <textarea
            autoFocus value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
            placeholder={`Comment on ${anchorLabel}… (⌘↵ to add)`} rows={2}
            className="w-full text-[12px] text-foreground bg-surface border border-border rounded px-2 py-1.5 resize-none outline-none focus:border-primary"
          />
          <div className="flex justify-end gap-1.5 mt-1">
            <button onClick={() => { setOpen(false); setText(''); }} className="px-2 py-1 text-[11px] rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-800">Cancel</button>
            <button onClick={submit} disabled={!text.trim()} className="px-2.5 py-1 text-[11px] font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40">Add comment</button>
          </div>
        </div>
      )}
    </div>
  );
}
